import { KeyVals, iterateEntries, forEachIterator } from '../../../../common/src/ts/utils';
import { StringView, SingleStringView } from './StringView';

type TrieLeaf<T> = [string, T];
type TrieBranch<T> = [string, undefined, TrieNode<T>];

interface TrieNode<T> {
  [key:string]: TrieLeaf<T> | TrieBranch<T> | undefined;
}

function isLeaf<T>(val: TrieLeaf<T> | TrieBranch<T>): val is TrieLeaf<T> {
  return val.length === 2;
}

function commonPrefix(a: string, b: string): [string, string, string] {
  const length = Math.min(a.length, b.length);
  let index = 0;
  while (index < length) {
    if (a[index] !== b[index]) {
      break;
    }
    index += 1;
  }
  return [a.slice(0, index), a.slice(index), b.slice(index)];
}

export default class Trie<T> {
  private root?: TrieLeaf<T> | TrieBranch<T>;
  private len: number = 0;

  public constructor();
  public constructor(dict: KeyVals<T>);
  public constructor(dict?: KeyVals<T>) {
    if (dict !== undefined) {
      forEachIterator(iterateEntries(dict), ([key, val]) => this.set(key, val));
    }
  }

  private setImpl(key: string,
                  value: T,
                  current?: TrieLeaf<T> | TrieBranch<T>): TrieLeaf<T> | TrieBranch<T> {
    if (current === undefined) {
      this.len += 1;
      return [key, value];
    }
    const [valueKey] = current;
    if (key.lastIndexOf(valueKey, 0) === 0) {
      const { length } = valueKey;
      const ch = key.substr(length, 1);
      if (length === key.length) {
        if (isLeaf(current)) {
          return [key, value];
        }
      } else if (isLeaf(current)) {
        const node = Object.create(null) as TrieNode<T>;
        node[''] = ['', current[1]];
        node[ch] = this.setImpl(key.slice(length), value);
        return [valueKey, , node];
      }
      const [, , node] = current;
      node[ch] = this.setImpl(key.slice(length), value, node[ch]);
      return current;
    }
    const [prefix, a, b] = commonPrefix(valueKey, key);
    const node = Object.create(null) as TrieNode<T>;
    current[0] = a;
    node[a.slice(0, 1)] = current;
    node[b.slice(0, 1)] = [b, value];
    this.len += 1;
    return [prefix, , node];
  }

  public set(key: string, value: T) {
    this.root = this.setImpl(key, value, this.root);
  }

  private static get<T>(key: string, current?: TrieLeaf<T> | TrieBranch<T>): T | undefined {
    if (current === undefined) {
      return;
    }
    const [valueKey] = current;
    if (key.lastIndexOf(valueKey, 0) === 0) {
      const { length } = valueKey;
      if (isLeaf(current)) {
        if (key.length === length) {
          return current[1];
        }
        return;
      }
      return Trie.get<T>(key.slice(length), current[2][key.substr(length, 1)]);
    }
  }

  public get(key: string) {
    return Trie.get(key, this.root);
  }

  private deleteImpl(key: string, current?: TrieLeaf<T> | TrieBranch<T>): boolean {
    if (current === undefined) {
      return false;
    }
    const [valueKey] = current;
    if (key.lastIndexOf(valueKey, 0) === 0) {
      const { length } = valueKey;
      if (isLeaf(current)) {
        if (key.length === length) {
          this.len -= 1;
          return true;
        }
        return false;
      }
      const node = current[2];
      const ch = key.substr(length, 1);
      if (this.deleteImpl(key.slice(length), node[ch])) {
        delete node[ch];
        const keys = Object.keys(node);
        switch (keys.length) {
          case 0:
            return true;
          case 1:
            const val = node[keys[0]]!;
            current[0] += val[0];
            if (isLeaf(val)) {
              current.pop();
              current[1] = val[1] as any;
            } else {
              current[2] = val[2];
            }
            break;
        }
      }
    }
    return false;
  }

  public delete(key:string) {
    if (this.deleteImpl(key, this.root)) {
      delete this.root;
    }
  }

  public has(key: string) {
    return this.get(key) !== undefined;
  }

  private static longestMatch<T>(
        view: StringView,
        current?: TrieLeaf<T> | TrieBranch<T>): [StringView, T] | undefined {
    if (current === undefined) {
      return;
    }
    const [key] = current;
    let index = 0;
    let currentView = view;
    for (; index < key.length; index += 1, currentView = currentView.slice(1)) {
      const ch = currentView.read(1);
      if (key[index] !== ch) {
        return;
      }
    }
    if (index < key.length) {
      return;
    }
    if (isLeaf(current)) {
      return [currentView, current[1]];
    }
    const children = current[2];
    const ch = currentView.read(1);
    const result = Trie.longestMatch<T>(currentView, current[2][ch]);
    if (result === undefined && '' in children) {
      return [currentView, children['']![1]!];
    }
    return result;
  }

  public longestMatch(text: string): [StringView, T] | undefined;
  public longestMatch(text: StringView): [StringView, T] | undefined;
  public longestMatch(text: string | StringView) {
    if (typeof text === 'string') {
      return this.longestMatch(new SingleStringView(text));
    }
    return Trie.longestMatch<T>(text, this.root);
  }

  public get length() {
    return this.len;
  }
}
